import {AssociationRead} from "./association";
import {ReferenceRead} from "./reference";
import {TagCreate, TagRead} from "./tag";
import {DocumentRequestCreate} from "./document_request";

export enum ChatInputState {
    ANY_TEXT = "ANY_TEXT",
    DOC_REQ_IN = "DOC_REQ_IN",
    DEEP_QUERY_INIT = "DEEP_QUERY_INIT",
    DEEP_QUERY_VALIDATE = "DEEP_QUERY_VALIDATE",
    LOADING = "LOADING", // For future use
}

export const ChatInputStateDisplay: Record<ChatInputState, string> = {
    [ChatInputState.ANY_TEXT]: "Normal Text",
    [ChatInputState.DOC_REQ_IN]: "Document Request Input",
    [ChatInputState.DEEP_QUERY_INIT]: "Deep Query",
    [ChatInputState.DEEP_QUERY_VALIDATE]: "Deep Query",
    [ChatInputState.LOADING]: "Loading...",
}


export type ChatMessageRole = "user" | "assistant" | "system" | "developer";

export enum ChatContextType {
    DOCUMENT = "DOCUMENT",
    FOCUS = "FOCUS",  // user is viewing this doc but didn't explicitly @-mention it
    FOLDER = "FOLDER",
    TAG = "TAG",
    ENTITY = "ENTITY",
}

export type ChatContext = {
    id: string;
    type: ChatContextType;
}
export type SentChatPlainTextPayload = {
    chatId?: string,
    inputState: ChatInputState;
    input: string;
    chatContexts?: ChatContext[];
};
export type SendDocumentRequestsPayload = {
    chatId?: string,
    inputState: ChatInputState;
    input: string,
    tagsToInfer?: TagCreate[];
    documentRequest?: DocumentRequestCreate;
};

export type SendChatPayload = SentChatPlainTextPayload & SendDocumentRequestsPayload;
export type SendChatRequest =
    {
        chatId: string,
        newChatMessage: ChatMessage,
        chatContexts: ChatContext[],
        tagsToInfer?: TagCreate[],
        documentRequest?: DocumentRequestCreate,
    };

export type ContentChunkQuote = {
    verbatim_quote?: string;
    assistant_comment?: string;
    chat_context_source_id?: string; // e.g., document ID
}

export type ChatContent = {
    content_chunks: ContentChunkQuote[];
}

export function chatContentFromText(text: string): ChatContent {
    return {
        content_chunks: [{assistant_comment: text}],
    };
}

export interface ChatMessage {
    role: ChatMessageRole;
    sender_id: string; // e.g., email or other ID
    content: ChatContent;
    timestamp: string; // ISO 8601 format
    id?: string; // This is timestamp + sender_id
    sent_from_chat_input_state?: ChatInputState; // Optional field to indicate the input state from which the message was sent
}

export function fromDocRequest(documentRequest?: DocumentRequestCreate, tagsToInfer?: TagCreate[]): SendDocumentRequestsPayload {
    let content: string;
    if (!tagsToInfer || tagsToInfer.length === 0) {
        content = "Document request with no tags.";
    } else {
        const lines = [
            `**Document Request** (${tagsToInfer.length} item${tagsToInfer.length !== 1 ? 's' : ''})`,
        ];
        if (documentRequest?.description) {
            lines.push(`\n> ${documentRequest.description}`);
        }
        lines.push('');
        tagsToInfer.forEach((t, i) => {
            lines.push(`${i + 1}. ${t.tag_name}`);
        });
        content = lines.join('\n');
    }

    return {
        input: content,
        inputState: ChatInputState.DOC_REQ_IN,
        tagsToInfer: tagsToInfer ?? [],
        documentRequest: documentRequest,
    };
}


export interface ChatCreate {
    group_id: string; // UUID
    submitter_id: string; // UUID
    title?: string;
    messages?: ChatMessage[];
    is_indexed_in_weaviate?: boolean;
    chat_input_state: ChatInputState;
}

export interface ChatRead extends ChatCreate {
    id: string; // UUID
    created_at: string; // ISO string
}

/** Lightweight chat item for tab lists — no messages or summaries. */
export interface ChatListItem {
    id: string;
    group_id: string;
    submitter_id: string;
    title?: string;
    chat_input_state?: ChatInputState;
    created_at: string;
}

export interface ChatReadWRefs extends ChatRead {
    references?: ReferenceRead[];
    associations?: AssociationRead[];
    tags?: TagRead[];
}

export interface ChatUpdate {
    id: string;
    group_id?: string;
    submitter_id?: string;
    title?: string;
    messages?: ChatMessage[];
    is_indexed_in_weaviate?: boolean;
    chat_input_state?: ChatInputState;
}

export interface ChatSearchResult {
    chat_id: string;
    title?: string;
    matched_on: "title" | "message";
    snippet?: string;
}
